import React, { useEffect, useState } from 'react';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useModalContainer } from '../../../contexts/ModalContainerContext';
import { resolveTaskName } from '../../../utils/utils';
import type { TaskSnapshot } from '../../../stores/types';
import { busyActionButtonProps } from './taskModalButtonStyles';
import { TaskModalBusyOverlay } from './TaskModalBusyOverlay';

export interface DeleteTaskConfirmModalProps {
  task: TaskSnapshot | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

export function DeleteTaskConfirmModal({
  task,
  submitting,
  onClose,
  onConfirm,
}: DeleteTaskConfirmModalProps) {
  const modalContainer = useModalContainer();
  const opened = task != null;
  const [actionPending, setActionPending] = useState(false);
  const busy = submitting || actionPending;

  useEffect(() => {
    if (!opened) {
      setActionPending(false);
    }
  }, [opened]);

  const handleConfirm = () => {
    if (busy) return;
    setActionPending(true);
    void Promise.resolve(onConfirm()).finally(() => {
      setActionPending(false);
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Delete task?"
      centered
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      portalProps={modalContainer ? { target: modalContainer } : undefined}
    >
      <TaskModalBusyOverlay busy={busy} label="Deleting the task..">
      <Stack gap="md">
        <Text size="sm">
          Delete <strong>{task ? resolveTaskName(task) : ''}</strong>? This cannot be undone.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={busy} type="button">
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={busy}
            onClick={handleConfirm}
            {...busyActionButtonProps(busy, 'red')}
          >
            Delete task
          </Button>
        </Group>
      </Stack>
      </TaskModalBusyOverlay>
    </Modal>
  );
}
